import { useState } from 'react';
import { ZoomIn } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { CullBadges } from '@/components/culling/CullBadges';
import { fullUrl, displayName } from '@/components/culling/cullUtils';
import { CULL } from '@/constants/testIds';

export const CullCompare = ({ open, onOpenChange, images }) => {
  const [zoom, setZoom] = useState(1);
  const cols = images.length <= 1 ? 1 : images.length === 2 ? 2 : images.length === 3 ? 3 : 2;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setZoom(1); onOpenChange(v); }}>
      <DialogContent data-testid={CULL.compareDialog} className="max-w-[96vw] w-[96vw] xl:max-w-7xl border-[hsl(var(--lxra-border))] bg-[#0A0A0A]/95 p-4 text-[hsl(var(--lxra-fg))] backdrop-blur-xl sm:p-6">
        <DialogHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0 pr-8">
          <div>
            <DialogTitle className="font-display text-2xl tracking-[0.03em]">Side by side</DialogTitle>
            <DialogDescription className="text-xs text-[hsl(var(--lxra-muted))]">Zoom is synced across frames — drag to loupe into eyes and edges.</DialogDescription>
          </div>
          <div className="flex items-center gap-3">
            <ZoomIn className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
            <Slider data-testid={CULL.compareZoom} value={[zoom]} min={1} max={3} step={0.1} onValueChange={([v]) => setZoom(v)} className="w-40" aria-label="Zoom" />
            <span className="w-10 font-mono text-xs text-[hsl(var(--lxra-muted))]">{zoom.toFixed(1)}×</span>
          </div>
        </DialogHeader>
        <div className={`mt-2 grid gap-3 ${cols === 1 ? 'grid-cols-1' : cols === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
          {images.map((img, i) => (
            <figure key={img.id} data-testid={CULL.comparePane(img.id)} className="overflow-hidden rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10]">
              <div className="relative aspect-[4/3] overflow-hidden bg-[#141416]">
                <img src={fullUrl(img)} alt={displayName(img, i)} style={{ transform: `scale(${zoom})` }} className="h-full w-full object-contain transition-transform duration-150 will-change-transform" />
              </div>
              <figcaption className="space-y-1.5 px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-xs text-[hsl(var(--lxra-fg))]">{displayName(img, i)}</p>
                  {img.cull?.sharpness != null && <span className="shrink-0 font-mono text-[10px] text-[hsl(var(--lxra-muted))]">sharp {Math.round(img.cull.sharpness)}</span>}
                </div>
                <CullBadges image={img} />
                {img.cull?.note && <p className="text-[11px] leading-snug text-[hsl(var(--lxra-muted))]">{img.cull.note}</p>}
              </figcaption>
            </figure>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
